import type { CalendarOptions } from '@fullcalendar/react';
import type { Theme } from '@src/app/themes';

/** The absence timeline owns its presentation; only layout comes from the v7 skeleton.
 * These hooks add stable classes without depending on FullCalendar's internal DOM.
 */
// The warning gutter, as on the users list: the leftmost strip of the
// resource column, drawn in the page background so it reads as outside the
// table. The calendar is pulled left by this much, into the page padding.
// It has to live inside the cells: rows are absolutely positioned and each
// cell crops its content, so nothing outside the column scrolls with them.
export const RESOURCE_GUTTER_WIDTH = 40;
const GUTTER = `${RESOURCE_GUTTER_WIDTH}px`;
// Space between the table edge and a row's content.
const RESOURCE_CELL_PADDING = `${RESOURCE_GUTTER_WIDTH + 12}px`;

export const absenceCalendarTheme = {
  className: 'absence-calendar',
  resourceGroupHeaderClass: (info) =>
    info.fieldValue ? 'absence-group-header' : 'absence-empty-group',
  resourceGroupHeaderInnerClass: 'absence-group-header-inner',
  resourceGroupLaneClass: (info) =>
    info.fieldValue ? 'absence-group-lane' : 'absence-empty-group',
  resourceGroupLaneInnerClass: 'absence-group-lane-inner',
  resourceExpanderClass: (info) =>
    info.isExpanded
      ? 'absence-native-expander absence-native-expanded'
      : 'absence-native-expander',
  resourceIndentClass: 'absence-resource-indent',
  resourceColumnHeaderClass: 'absence-resource-header',
  resourceColumnHeaderInnerClass: 'absence-resource-header-inner',
  resourceCellInnerClass: 'absence-resource-cell-inner',
  resourceColumnDividerClass: 'absence-resource-divider',
  resourceRowClass: 'absence-resource-row',
  tableHeaderClass: 'absence-table-header',
  tableBodyClass: 'absence-table-body',
  slotHeaderRowClass: 'absence-header-row',
  slotHeaderDividerClass: 'absence-header-divider',
  slotHeaderInnerClass: 'absence-date-header-inner',
  slotHeaderSticky: false,
  slotHeaderAlign: ({ level }) => (level === 2 ? 'start' : 'center'),
  rowEventClass: ({ isStart, isEnd }) =>
    [
      'absence-event',
      isStart && 'absence-event-start',
      isEnd && 'absence-event-end',
    ]
      .filter(Boolean)
      .join(' '),
  rowEventInnerClass: 'absence-event-inner',
  rowEventBeforeClass: (info) =>
    info.isStartResizable
      ? 'absence-event-resizer absence-event-resizer-start'
      : undefined,
  rowEventAfterClass: (info) =>
    info.isEndResizable
      ? 'absence-event-resizer absence-event-resizer-end'
      : undefined,
  backgroundEventClass: 'absence-background',
  highlightClass: 'absence-highlight',
  nonBusinessHoursClass: 'absence-non-business',
} satisfies CalendarOptions;

export const absenceCalendarStyles = (theme: Theme) => {
  const absenceBackground = `var(--abs-colour-bg, ${theme.palette.grey[200]})`;
  // Selection over hover, both positioned by `useMarkedDayBand`.
  //
  // Every stop carries an explicit position, and each layer ends with a hard
  // stop at the band's end followed by transparent out to 100%. Ending on a
  // bare `transparent 0` instead relies on the spec's clamp-to-previous fix-up
  // to keep the remainder clear, and left the far end of a bar tinted when a
  // band sat at the near one.
  //
  // The offsets default far off the element rather than to 0. A zero-width hard
  // stop at 0 is not reliably invisible -- it rounds up to a hairline at the
  // bar's left edge, which showed as a sliver of colour at the far end while
  // hovering the other one.
  const dayBands = `linear-gradient(90deg,
      transparent 0 var(--abs-band-left, -9999px),
      var(--abs-colour-fg, ${theme.palette.grey[700]}) var(--abs-band-left, -9999px) calc(var(--abs-band-left, -9999px) + var(--abs-band-w, 0px)),
      transparent calc(var(--abs-band-left, -9999px) + var(--abs-band-w, 0px)) 100%),
    linear-gradient(90deg,
      transparent 0 var(--abs-hover-left, -9999px),
      var(--abs-colour-fg, ${theme.palette.grey[700]}) var(--abs-hover-left, -9999px) calc(var(--abs-hover-left, -9999px) + var(--abs-hover-w, 0px)),
      transparent calc(var(--abs-hover-left, -9999px) + var(--abs-hover-w, 0px)) 100%)`;
  // Weekend and holiday days inside an aggregated bar, under those two.
  //
  // A single day gets the lighter shade from the whole-bar `.abs-weekend` rule
  // below, but a run is one element, so the same shade has to be a band here.
  // `useMarkedDayBand` measures where each of those days actually is and writes
  // the finished gradient to `--abs-light-days`; there is a stop pair per light
  // day in one layer, so the layer count does not grow with the bar.
  //
  // Last, because the selection and hover bands are opaque `--abs-colour-fg`
  // and belong over it. `none` keeps the declaration valid before the first
  // paint and on a bar that has no light day in it.
  const barBands = `${dayBands}, var(--abs-light-days, none)`;
  const tentativeStripeColour = `color-mix(in srgb, ${absenceBackground}, var(--abs-colour-fg, rgba(0,0,0,0.12)) 15%)`;
  const tentativeStripes = `repeating-linear-gradient(-45deg, transparent, transparent 4px, ${tentativeStripeColour} 4px, ${tentativeStripeColour} 5px)`;
  // Paints a background or border only right of the gutter.
  const pastGutter = (colour: string) =>
    `linear-gradient(to right, transparent ${GUTTER}, ${colour} ${GUTTER})`;
  // A border image for one horizontal border, sliced from that side only: a
  // slice on the other sides would stretch the image and pull the line ~1px
  // into the gutter.
  const gutterlessBorder = (side: 'top' | 'bottom') =>
    `${pastGutter(theme.palette.grey[200])} ${side === 'top' ? '1 0 0 0' : '0 0 1 0'}`;
  return {
    // The table frame starts where the gutter ends.
    '&::after': {
      border: `1px solid ${theme.palette.grey[200]}`,
      borderRadius: '0 0 6px 6px',
      content: '""',
      inset: `0 0 0 ${GUTTER}`,
      pointerEvents: 'none',
      position: 'absolute',
      zIndex: 3,
    },
    '& .absence-calendar': {
      '--abs-grid': `1px solid ${theme.palette.grey[200]}`,
      background: `linear-gradient(to right, ${theme.palette.grey[150]} ${GUTTER}, ${theme.palette.common.white} ${GUTTER})`,
      color: theme.palette.text.primary,
      fontFamily: theme.typography.fontFamily,
      '.absence-native-expander, .absence-resource-indent, .absence-empty-group':
        { display: 'none' },
      // The resource pane has no day column behind it, so this one stays flat.
      '.absence-group-header': {
        // Anchors the closed group's warning gutter, as the cell does a row's.
        position: 'relative',
        background: pastGutter(theme.palette.grey[100]),
        borderBottom: `1px solid ${theme.palette.grey[200]}`,
        borderImage: gutterlessBorder('bottom'),
      },
      '.absence-group-lane': {
        background: theme.palette.grey[100],
        borderBottom: `1px solid ${theme.palette.grey[200]}`,
      },
      // v7 wraps our inner content in an unclassed `noShrink flexRow` div, inside
      // a cell that is `flexCol` + `alignStart` -- `align-items: flex-start
      // !important`. A column's cross axis is horizontal, so that wrapper is
      // sized to its content and the `width: 100%` below resolves against the
      // label, not the cell. The whole group row has to be clickable, so stretch
      // the wrapper: `align-self` is a different property from the parent's
      // `align-items`, so it wins without a counter-`!important`.
      '.absence-group-header > *, .absence-group-lane > *': {
        alignSelf: 'stretch',
      },
      '.absence-group-header-inner, .absence-group-lane-inner': {
        width: '100%',
      },
      '.absence-group-button, .absence-group-lane-button': {
        width: '100%',
        minHeight: '46px',
        fontFamily: theme.typography.fontFamily,
        color: theme.palette.text.primary,
        '&:focus-visible': {
          outline: `2px solid ${theme.palette.grey[800]}`,
          outlineOffset: '-2px',
        },
      },
      '.absence-group-button': {
        ...theme.typography.body1,
        justifyContent: 'flex-start',
        paddingInlineStart: RESOURCE_CELL_PADDING,
        fontWeight: 500,
      },
      '.absence-group-lane-button': {
        justifyContent: 'flex-end',
        '& .MuiSvgIcon-root': {
          position: 'sticky',
          insetInlineEnd: '12px',
          width: '18px',
          height: '18px',
        },
      },
      '.absence-table-header': {
        background: theme.palette.grey[100],
      },
      // FullCalendar gives the resource side and the date side each a header
      // of this class, and a divider under it. Only the resource side's
      // reaches into the gutter.
      '.absence-table-header:has(.absence-resource-header)': {
        background: pastGutter(theme.palette.grey[100]),
        '& > .absence-header-divider': { borderImage: gutterlessBorder('top') },
      },
      '.absence-resource-header': {
        background: pastGutter(theme.palette.grey[100]),
        borderInlineEnd: 'var(--abs-grid) !important',
      },
      // FullCalendar 7.1 reserves a 1px row separator even for our hidden,
      // zero-height empty group. Move both body scrollers under the header
      // divider by that pixel; the header's higher stacking layer keeps the
      // divider visible. This preserves alignment and native hit coordinates.
      // The class depends on resource data, not virtualized group DOM, so
      // scrolling the empty group out of view cannot toggle the correction.
      '.absence-body-with-empty-group': {
        marginBlockStart: '-1px',
      },
      // FullCalendar 7.1 puts an unexposed flex wrapper between the
      // resource header and headerInnerClass. Its align-items:flex-start
      // prevents our header content from stretching across the column.
      // Keep this override until FullCalendar exposes that wrapper.
      '.absence-resource-header > div': {
        alignItems: 'stretch !important',
      },
      '.absence-resource-header-inner': {
        width: '100%',
        minHeight: '68px',
        paddingInlineStart: RESOURCE_CELL_PADDING,
        boxSizing: 'border-box',
      },
      '.absence-resource-cell': {
        alignItems: 'unset !important',
        borderInlineEnd: 'var(--abs-grid) !important',
        // Anchors the row's warning gutter. Rows are positioned by
        // FullCalendar; the cell inside one is in normal flow.
        position: 'relative',
      },
      '.absence-resource-cell-inner': {
        width: '100%',
        paddingInlineStart: RESOURCE_CELL_PADDING,
        boxSizing: 'border-box',
      },
      '.absence-resource-row': {
        borderBottom: 'var(--abs-grid)',
        borderImage: gutterlessBorder('bottom'),
      },
      '.absence-resource-divider': {
        borderInlineEnd: 0,
        // v7 makes this divider a drag handle for the resource area's width
        // whenever the interaction plugin is loaded, with no option to turn it
        // off. The width is fixed by `resourceColumnsWidth`, so take the
        // divider and its widened hit strip out of hit-testing entirely.
        '&, & *': { pointerEvents: 'none' },
      },
      '.absence-resource-lane': {
        borderBottom: 'var(--abs-grid)',
        // Centre the bars in the row. v6 did this with `alignItems: center` on
        // `.fc-timeline-lane-frame`, a *row* flex container; v7's lane is a
        // column holding the background layer, a top slot and then the events,
        // so the same thing is `justifyContent` here. Without it the bars
        // stack from the top of the row.
        justifyContent: 'center',
      },
      '.absence-resource-cell.absence-resource-selected': {
        background: pastGutter(theme.palette.common.black),
        '& .absence-resource-main *': { color: theme.palette.common.white },
        '& .absence-days-off': {
          background: theme.palette.grey[100],
          color: theme.palette.grey[800],
          borderColor: theme.palette.grey[700],
          boxShadow: `inset 1px 0 0 ${theme.palette.grey[700]}, inset 0 -1px 0 ${theme.palette.grey[700]}, inset 0 1px 0 ${theme.palette.grey[700]}`,
        },
        '& .absence-days-off *': { color: theme.palette.grey[800] },
      },
      // On a layer of its own: the lane is `position: relative`, and an inset
      // shadow on the lane itself is painted under the background events that
      // fill the free days, so it showed only on weekends and holidays.
      '.absence-resource-lane.absence-resource-selected::after': {
        content: '""',
        position: 'absolute',
        inset: 0,
        zIndex: 4,
        pointerEvents: 'none',
        boxShadow: `inset 0 -1px 0 ${theme.palette.grey[700]}, inset 0 1px 0 ${theme.palette.grey[700]}, inset -1px 0 0 ${theme.palette.grey[700]}`,
      },
      // Day view only. Month view has day shading behind the lane, which a lane
      // fill would cover, so there the free days get their own background
      // events (`.absence-selected-row-background`) instead.
      //
      // Multiplied rather than painted over: the hour lines are on the slot
      // columns behind the lane, and an opaque fill hid them. On white this is
      // exactly grey[100]; over a line it leaves the line showing.
      '.absence-resource-lane.absence-resource-selected-fill::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        background: theme.palette.grey[100],
        mixBlendMode: 'multiply',
        pointerEvents: 'none',
      },
      // Opaque, so it repeats the day's grid line from `.absence-slot-lane`.
      '.absence-background.absence-selected-row-background': {
        boxSizing: 'border-box',
        borderInlineStart: 'var(--abs-grid)',
        background: theme.palette.grey[100],
        opacity: 1,
        pointerEvents: 'none',
      },
      '.absence-date-header': {
        color: theme.palette.common.black,
        textAlign: 'center',
        borderBottom: 'var(--abs-grid)',
        borderInlineStart: 'var(--abs-grid)',
      },
      '.absence-slot-lane': {
        borderInlineStart: 'var(--abs-grid)',
      },
      '.absence-date-header-inner': {
        width: '100%',
        padding: '0 2px',
        boxSizing: 'border-box',
        fontSize: '12px',
        fontWeight: 400,
      },
      '.absence-header-divider': {
        borderTop: 'var(--abs-grid)',
      },
      '.absence-header-row': { flexGrow: '0 !important' },
      // The header is as tall as the resource column header, and the day
      // view's two rows don't fill it. Grow the hour row so its cell borders
      // reach the header divider instead of stopping short of it.
      '.absence-header-row:has(.absence-day-view-hour-slot-label)': {
        flexGrow: '1 !important',
      },
      // Header cells are borderless top/bottom, so the line under a row is
      // drawn as the top border of the row beneath it.
      '.absence-header-row:has(.absence-weekday-slot-label), .absence-header-row:has(.absence-day-view-hour-slot-label)':
        {
          borderTop: 'var(--abs-grid) !important',
        },
      '.absence-month-slot-label': {
        textAlign: 'start',
        '& .absence-date-header-inner': {
          padding: '1px 7px',
          lineHeight: '17px',
        },
      },
      '.absence-weekday-slot-label': {
        lineHeight: '22px',
        height: '22px',
        borderBottom: 0,
        justifyContent: 'center',
        color: theme.palette.grey[800],
        '& .absence-date-header-inner': { fontSize: '10px' },
      },
      '.absence-daynum-slot-label ': {
        justifyContent: 'center',
        height: '27px',
      },
      '.absence-daynum-slot-label .absence-date-header-inner': {
        fontSize: '14px',
        lineHeight: '25px',
      },
      '.absence-day-view-header .absence-date-header-inner': {
        padding: '7px 2px 8px',
        fontSize: '10px',
      },
      '.absence-day-view-date-slot-label': {
        textAlign: 'start',
        '& .absence-date-header-inner': {
          padding: '1px 0px 0px 7px',
          fontSize: '12px',
        },
      },
      '.absence-day-view-hour-slot-label': {
        '& .absence-date-header-inner': {
          height: '48px',
          display: 'flex',
          alignItems: 'center',
        },
      },
      '.absence-weekend-slot-label, .absence-holiday-slot-label': {
        color: theme.palette.grey[600],
      },
      '.absence-today-slot-label': {
        background: theme.palette.red[600],
        color: theme.palette.common.white,
      },
      '.absence-hovered-slot-label': {
        background: theme.palette.grey[800],
        color: theme.palette.common.white,
      },
      // Day shading, on the full-height slot column rather than on background
      // events. Colours match what `.absence-*-background` drew when these
      // were events, so the only visible change is that the shading now also
      // covers the group rows and the strip above the first lane.
      '.absence-slot-lane.absence-weekend-column, .absence-slot-lane.absence-holiday-column':
        {
          background: theme.palette.grey[150],
          opacity: '0.7',
        },
      '.absence-slot-lane.absence-today-column': {
        background: theme.palette.red[100],
      },
      '.absence-slot-lane.absence-hovered-lane': {
        background: theme.palette.grey[600],
      },
      // One day of an aggregated bar, marked without splitting the element.
      //
      // `useMarkedDayBand` measures where each day actually is and writes these
      // offsets; the classes it reads (`abs-day-from-N`, `abs-day-span-N`) come
      // from the class generator, which reruns on selection change. Widths
      // default to zero, so an unmeasured bar shows no band rather than one in
      // the wrong place.
      //
      // Two layers, selection over hover. `border-box` because the offsets are
      // measured from the border box, and a gradient otherwise resolves against
      // the padding box -- 1px of border each side, which showed up as the last
      // day sitting ~2px left.
      //
      // The hover rule repeats `backgroundImage` on purpose: the plain
      // `.absence-event:hover` below sets the `background` *shorthand*, which
      // resets `background-image` to none, and it would otherwise erase both
      // bands exactly when they are wanted.
      '.absence-event.abs-aggregated': {
        backgroundOrigin: 'border-box',
        backgroundClip: 'border-box',
        backgroundImage: barBands,
        // Colouring a whole bar on hover is right for a single day and wrong
        // for a run: the hover band says which day instead.
        '&:hover': {
          backgroundColor: `var(--abs-colour-bg,${theme.palette.grey[200]})`,
          backgroundImage: barBands,
          // The plain hover rule also turns the workday count white, which
          // reads as "this label is on the highlighted day". On a run the label
          // sits at the bar's start and the hovered day is usually somewhere
          // else, so leave it alone -- the same reason a partial selection does
          // not whiten it either.
          //
          // Not when the whole run is selected, though: the bar is then solid
          // `--abs-colour-fg` and the label has to stay white. This selector
          // outweighs `.abs-selected`'s own white, so excluding it here is what
          // keeps the count readable while hovering a selected run.
          '&:not(.abs-selected) .abs-workday-count, &:not(.abs-selected) .abs-event-content':
            {
              color: `var(--abs-colour-fg,${theme.palette.grey[800]})`,
            },
        },
      },
      // ...except where the band actually reaches the label. The workday count
      // sits at the bar's start, and band and label are both
      // `--abs-colour-fg`, so a band over the first day left the number
      // invisible. Invert it exactly then: `abs-day-from-0` when the selection
      // starts at day one, `abs-hover-first` when that is the day hovered.
      [[
        '.absence-event.abs-day-marked.abs-day-from-0',
        '.absence-event.abs-hover-first',
      ]
        .flatMap((bar) => [
          `${bar} .abs-workday-count`,
          `${bar} .abs-event-content`,
        ])
        .join(', ')]: {
        color: `${theme.palette.common.white} !important`,
      },
      '.absence-event-inner': {
        display: 'flex',
        alignItems: 'center',
        minWidth: 0,
      },
      '.absence-event-resizer': {
        position: 'absolute',
        top: 0,
        bottom: 0,
        width: '6px',
        zIndex: 1,
      },
      '.absence-event-resizer-start': { insetInlineStart: 0 },
      '.absence-event-resizer-end': { insetInlineEnd: 0 },
      // An employee's own non-working time: built from their `worktime` rows,
      // so in month view this is the weekday somebody does not work, not just
      // a second drawing of the weekend.
      //
      // Opaque, and pre-composited to exactly what the weekend column resolves
      // to. That is what stops the two stacking into a darker band where they
      // coincide: a Saturday that is also non-working now looks identical to
      // one that is only weekend-shaded, while a Friday off still gets its
      // shading.
      '.absence-non-business': {
        background: `color-mix(in srgb, ${theme.palette.grey[150]} 70%, ${theme.palette.common.white})`,
        opacity: 1,
        pointerEvents: 'none',
      },
      // Day view has no shaded columns behind the lanes, only the hour lines,
      // so the fill can multiply over them instead of hiding them. Month view
      // stays opaque: there it has to cover today's column.
      '&:has(.absence-day-view-header) .absence-non-business': {
        mixBlendMode: 'multiply',
      },
      '.absence-background.absence-holiday-background': {
        // Opaque, and pre-composited to what grey[150] at 0.7 used to resolve
        // to over the calendar. A branch holiday has to *hide* the today
        // column for those employees -- previously today's event was simply
        // not created for them, and a translucent fill would let the red show
        // through instead.
        background: `color-mix(in srgb, ${theme.palette.grey[150]} 70%, ${theme.palette.common.white})`,
        opacity: '1',
        pointerEvents: 'none',
        '&:hover': {
          background: theme.palette.grey[150],
          opacity: '0.7',
        },
      },
      '.absence-highlight': {
        border: '1px dashed',
        borderColor: theme.palette.red[600],
        background: 'transparent !important',
        opacity: '1',
        // Above the selected row's border (`::after`, zIndex 4), which shares
        // its top and bottom edge.
        zIndex: 5,
        pointerEvents: 'none',
      },
      '.abs-base': {
        height: '22px',
        padding: '0 6px',
      },
      '.abs-selected': {
        background: `var(--abs-colour-fg,${theme.palette.grey[700]}) !important`,
        '& .abs-workday-count, & .abs-event-content': {
          color: theme.palette.common.white,
        },
        '&:hover': {
          background: `var(--abs-colour-fg,${theme.palette.grey[700]}) !important`,
        },
      },
      '.absence-bar-content': { display: 'flex', width: '100%', minWidth: 0 },
      // Centred on the bar's *first day*, not at the bar's start. The box is made
      // one day wide and pulled back over the bar's border and padding, so its
      // edges sit on that cell's edges and `textAlign: center` lands the number
      // on the cell's centre line. At ~25px per day the difference is invisible;
      // on a calendar stretched to the page width it is most of a cell.
      //
      // `--abs-day-w` is measured by `useMarkedDayBand`, which re-reads it on
      // scroll and resize, so the centring follows the column width. Before the
      // first paint the box falls back to its content, which with the negative
      // margin is the pre-existing start-aligned position shifted by 7px -- one
      // frame, in the same effect that mounts the hook.
      '.abs-workday-count': {
        position: 'sticky',
        insetInlineStart: '6px',
        flexShrink: 0,
        color: `var(--abs-colour-fg,${theme.palette.grey[800]})`,
        fontWeight: 500,
        fontSize: '14px',
        textAlign: 'center',
        lineHeight: '20px',
        minWidth: '10px',
        width: 'var(--abs-day-w, max-content)',
        // 1px border from `.absence-event` + 6px inline padding from `.abs-base`.
        marginInlineStart: '-7px',
      },
      '.abs-selected.abs-partial': {
        background: `var(--abs-colour-fg,${theme.palette.grey[700]}) !important`,
        '&:hover': {
          background: `var(--abs-colour-fg,${theme.palette.grey[700]}) !important`,
        },
      },
      '.absence-event': {
        borderRadius: 0,
        position: 'relative',
        boxSizing: 'border-box',
        fontSize: '12px',
        border: '1px solid',
        borderColor: `var(--abs-colour-fg,${theme.palette.grey[700]})`,
        textDecoration: 'none',
        cursor: 'pointer',
        '&:focus-visible': {
          outline: `2px solid ${theme.palette.grey[800]}`,
          outlineOffset: '2px',
        },
        // The lighter shade a weekend or a holiday is drawn in, defined once:
        // the rule below paints a whole single-day bar with it, and
        // `useMarkedDayBand` paints a band of it inside an aggregated one. The
        // two have to agree, and a run that spans a weekend used to show the
        // full colour because only the single-day rule existed.
        '--abs-colour-light': `color-mix(in srgb, var(--abs-colour-bg,${theme.palette.grey[200]}), white 70%)`,
        background: `var(--abs-colour-bg,${theme.palette.grey[200]})`,
        '&:hover': {
          background: `var(--abs-colour-fg,${theme.palette.grey[700]})`,
          '& .abs-workday-count, & .abs-event-content': {
            color: theme.palette.common.white,
          },
        },
      },
      '.absence-event.abs-weekend, .absence-event.abs-holiday': {
        background: 'var(--abs-colour-light)',
        '&:hover': {
          background: `var(--abs-colour-fg,${theme.palette.grey[700]})`,
        },
      },
      '.absence-event.abs-readonly': {
        cursor: 'default',
        '&:hover': {
          background: `var(--abs-colour-bg,${theme.palette.grey[200]})`,
        },
      },
      '.absence-event.abs-neutral-colour': {
        '--abs-colour-bg': theme.palette.grey[200],
        '--abs-colour-fg': theme.palette.grey[700],
      },
      '.absence-event.abs-readonly.abs-selected': {
        '&:hover': {
          background: `var(--abs-colour-fg,${theme.palette.grey[700]}) !important`,
        },
      },
      '.absence-event.abs-tentative': {
        backgroundImage: tentativeStripes,
      },
      // The stripes are a `background-image` and so are the day bands, and this
      // selector is no weaker than `.abs-aggregated`'s and comes later, so a
      // tentative run was losing all of them -- the lighter weekend days, and
      // the selection and hover bands with them. Stripes stay on top; the bands
      // show through their gaps.
      '.absence-event.abs-tentative.abs-aggregated': {
        backgroundImage: `${tentativeStripes}, ${barBands}`,
      },
      '.absence-event.abs-tentative:hover, .absence-event.abs-tentative.abs-selected':
        {
          backgroundImage:
            'repeating-linear-gradient(-45deg, transparent, transparent 4px, rgba(255,255,255,0.4) 4px, rgba(255,255,255,0.4) 5px) !important',
        },
      '.absence-event.abs-pending-approval': {
        borderStyle: 'dashed',
        borderWidth: '1px',
      },
      // These are renderer clipping flags, not daily-record join positions.
      // A bar extending outside the visible date range stays open at that edge.
      '.absence-event.absence-event-start': {
        borderStartStartRadius: '6px',
        borderEndStartRadius: '6px',
      },
      '.absence-event.absence-event-end': {
        borderStartEndRadius: '6px',
        borderEndEndRadius: '6px',
      },
      '.absence-event:not(.absence-event-start)': {
        borderInlineStartWidth: 0,
      },
      '.absence-event:not(.absence-event-end)': {
        borderInlineEndWidth: 0,
      },
      // Day view draws a multi-day all-day block one day at a time; the edges
      // the block carries on past stay open, as on the month view's bar.
      '.absence-event.absence-event-start.abs-continues-before': {
        borderStartStartRadius: 0,
        borderEndStartRadius: 0,
        borderInlineStartWidth: 0,
      },
      '.absence-event.absence-event-end.abs-continues-after': {
        borderStartEndRadius: 0,
        borderEndEndRadius: 0,
        borderInlineEndWidth: 0,
      },
      '.absence-event.transient-event': {
        border: 0,
        background: 'transparent !important',
        pointerEvents: 'none',
        '&:hover': {
          background: 'transparent !important',
        },
      },
      '.abs-event-content': {
        position: 'sticky',
        insetInlineStart: '6px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        width: '100%',
        fontWeight: 900,
        color: `var(--abs-colour-fg,${theme.palette.common.white})`,
      },
      '.absence-event.transient-event .abs-event-content': {
        color: theme.palette.common.black,
      },
      // A draft is one event per day, so its label is a day number sitting in
      // that day's own cell. `static` undoes the sticky offset
      // `.abs-event-content` carries for long bars, which would drag a one-day
      // number off centre as the timeline scrolls.
      '.absence-event.transient-month-event .abs-event-content': {
        position: 'static',
        justifyContent: 'center',
      },
    },
  };
};
